import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
// import { HOST_API } from 'src/config-global';
import { CautionsState, Project, Guarantee, File } from 'src/types/cautions';
import { IFileManager } from 'src/types/file';

// Initial State
const initialState: CautionsState = {
  projects: [],
  guarantees: [],
  loading: false,
  error: null,
};

// Async Thunks for fetching data (optional)
// export const fetchProjects = createAsyncThunk<Project[], void, { rejectValue: string }>(
//   'cautions/fetchProjects',
//   async (_, { rejectWithValue }) => {
//     try {
//       const accessToken = localStorage.getItem('lbankaAccessToken');
//       const headers = {
//         'Content-Type': 'application/json',
//         ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
//       };

//       const response = await fetch(`${HOST_API}/projects`, { headers });
      
//       if (!response.ok) throw new Error('Failed to fetch projects');
//       return (await response.json()).projects as Project[];
//     } catch (error) {
//       return rejectWithValue((error as Error).message);
//     }
//   }
// );
export const fetchProjects = createAsyncThunk<Project[], Project[], { rejectValue: string }>(
  'cautions/fetchProjects',
  async (projects, { rejectWithValue }) => {
    try {
      return projects; // Simply return the projects passed as an argument
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// export const fetchGuarantees = createAsyncThunk<Guarantee[], void, { rejectValue: string }>(
//   'cautions/fetchGuarantees',
//   async (_, { rejectWithValue }) => {
//     try {
//       const accessToken = localStorage.getItem('lbankaAccessToken');
//       const headers = {
//         'Content-Type': 'application/json',
//         ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
//       };

//       const response = await fetch(`${HOST_API}/guarantees`, { headers });
      
//       if (!response.ok) throw new Error('Failed to fetch guarantees');
//       return (await response.json()).guarantees as Guarantee[];
//     } catch (error) {
//       return rejectWithValue((error as Error).message);
//     }
//   }
// );

export const fetchGuarantees = createAsyncThunk<Guarantee[], Guarantee[], { rejectValue: string }>(
  'cautions/fetchGuarantees',
  async (guarantees, { rejectWithValue }) => {
    try {
      return guarantees; // Simply return the guarantees passed as an argument
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchFiles = createAsyncThunk<File[], void, { rejectValue: string }>(
  'cautions/fetchFiles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/files');
      if (!response.ok) throw new Error('Failed to fetch files');
      return (await response.json()) as File[];
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// Create Slice
const cautionsSlice = createSlice({
  name: 'cautions',
  initialState,
  reducers: {
    // Add, Update, Remove Projects
    addProject: (state, action: PayloadAction<Project>) => {
      state.projects.push(action.payload);
    },
    updateProject: (state, action: PayloadAction<Project>) => {
      const index = state.projects.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.projects[index] = action.payload;
      }
    },
    deleteProject: (state, action: PayloadAction<number>) => {
      state.projects = state.projects.filter((p) => p.id !== action.payload);
    },
    updateProjectStatus: (state, action: PayloadAction<{ id: number; status: string }>) => {
      const project = state.projects.find((p) => p.id === action.payload.id);
      if (project) {
        project.status = action.payload.status;
        project.updated_at = new Date().toISOString();
      }
    },
    // Add, Update, Remove Guarantees
    addGuarantee: (state, action: PayloadAction<Guarantee>) => {
      state.guarantees.push(action.payload);
    },
    updateGuarantee: (state, action: PayloadAction<Guarantee>) => {
      const index = state.guarantees.findIndex((g) => g.id === action.payload.id);
      if (index !== -1) {
        state.guarantees[index] = action.payload;
      }
    },
    deleteGuarantee: (state, action: PayloadAction<number>) => {
      state.guarantees = state.guarantees.filter((g) => g.id !== action.payload);
    },
    addFilesToCaution: (state, action: PayloadAction<{ cautionId: number; files: IFileManager[] }>) => {
      const { cautionId, files } = action.payload;
      const caution = state.guarantees.find((g) => g.id === cautionId);

      if (caution) {
        caution.documents = caution.documents ? [...caution.documents, ...files] : files;
      }
    },
    removeFileFromCaution: (state, action: PayloadAction<{ guaranteeId: number; fileId: string }>) => {
      const { guaranteeId, fileId } = action.payload;
      const guarantee = state.guarantees.find((g) => g.id === guaranteeId);
      if (guarantee && guarantee.documents) {
        guarantee.documents = guarantee.documents.filter((file) => file.id !== fileId);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.projects = action.payload;
        state.loading = false;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch projects';
        state.loading = false;
      })

      .addCase(fetchGuarantees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGuarantees.fulfilled, (state, action) => {
        state.guarantees = action.payload;
        state.loading = false;
      })
      .addCase(fetchGuarantees.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch guarantees';
        state.loading = false;
      })

      .addCase(fetchFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch files';
        state.loading = false;
      });
  },
});

// Export Actions
export const {
  addProject,
  updateProject,
  updateProjectStatus,
  deleteProject,
  addGuarantee,
  updateGuarantee,
  deleteGuarantee,
  addFilesToCaution,
  removeFileFromCaution,
} = cautionsSlice.actions;

// Export Reducer
export default cautionsSlice.reducer;
